import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Chip
} from '@mui/material';

import { TimeWindowSettings as TimeWindowSettingsType } from '../../types/simulation';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
interface TimeWindowSettingsProps {
  onSettingsChange: (settings: TimeWindowSettingsType) => void;
  onPresetSelectionChange?: (selectedPresets: string[]) => void;
  settings: TimeWindowSettingsType | null;
}

export const TimeWindowSettings: React.FC<TimeWindowSettingsProps> = ({
  onSettingsChange,
  onPresetSelectionChange,
  settings
}) => {
  const { t } = useTranslation('simulation');
  const [expanded, setExpanded] = useState(false);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  
  // Default settings
  const defaultSettings: TimeWindowSettingsType = {
    time_model_type: 'slope_and_intercept',
    settings: {
      slope: 2.0,
      intercept: 1200,
      window: 1200,
      max_time_minutes: 90,
      once_off_processing_time_seconds: 15,
      processing_time: [
        {
          seat_type: 'standard',
          processing_time_seconds: 30
        },
        {
          seat_type: 'wheelchair',
          processing_time_seconds: 90
        }
      ]
    },
    ride_settings: {
      rides_name: 'simulation-rides',
      batch_id: null
    }
  };

  const [currentSettings, setCurrentSettings] = useState<TimeWindowSettingsType>(
    settings || defaultSettings
  );

  const handleSettingChange = (path: string[], value: any) => {
    const newSettings = { ...currentSettings };
    let current: any = newSettings;
    
    // Navigate to the nested property
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    
    // Set the value
    current[path[path.length - 1]] = value;
    
    setCurrentSettings(newSettings);
    onSettingsChange(newSettings);
  };

  const handleProcessingTimeChange = (seatType: string, seconds: number) => {
    const newProcessingTime = currentSettings.settings.processing_time.map(pt => 
      pt.seat_type === seatType 
        ? { ...pt, processing_time_seconds: seconds }
        : pt
    );
    
    handleSettingChange(['settings', 'processing_time'], newProcessingTime);
  };

  const resetToDefaults = () => {
    setCurrentSettings(defaultSettings);
    onSettingsChange(defaultSettings);
  };

  const togglePreset = (preset: 'fast' | 'balanced' | 'thorough') => {
    const newSelectedPresets = selectedPresets.includes(preset)
      ? selectedPresets.filter(p => p !== preset)
      : [...selectedPresets, preset];
    
    setSelectedPresets(newSelectedPresets);
    onPresetSelectionChange?.(newSelectedPresets);
    
    // If only one preset is selected, apply it immediately
    if (newSelectedPresets.length === 1) {
      loadPreset(newSelectedPresets[0] as 'fast' | 'balanced' | 'thorough');
    } else if (newSelectedPresets.length === 0) {
      // Reset to defaults if no presets selected
      resetToDefaults();
    }
  };

  const loadPreset = (preset: 'fast' | 'balanced' | 'thorough') => {
    const presets = {
      fast: {
        ...defaultSettings,
        settings: {
          ...defaultSettings.settings,
          slope: 1.5,
          intercept: 600,
          window: 600,
          max_time_minutes: 60,
          once_off_processing_time_seconds: 10
        }
      },
      balanced: defaultSettings,
      thorough: {
        ...defaultSettings,
        settings: {
          ...defaultSettings.settings,
          slope: 2.5,
          intercept: 1800,
          window: 1800,
          max_time_minutes: 120,
          once_off_processing_time_seconds: 20
        }
      }
    };

    const presetSettings = presets[preset];
    setCurrentSettings(presetSettings);
    onSettingsChange(presetSettings);
  };

  const standardProcessingTime = currentSettings.settings.processing_time.find(pt => pt.seat_type === 'standard')?.processing_time_seconds || 30;
  const wheelchairProcessingTime = currentSettings.settings.processing_time.find(pt => pt.seat_type === 'wheelchair')?.processing_time_seconds || 90;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {t('setup.timeWindow.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={t('setup.timeWindow.windowChip', { seconds: currentSettings.settings.window })}
              size="small"
              variant="outlined"
            />
            <Chip
              label={t('setup.timeWindow.maxChip', { minutes: currentSettings.settings.max_time_minutes })}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>

        {/* Quick Presets */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            {t('setup.timeWindow.quickPresets')} {selectedPresets.length > 0 && t('setup.timeWindow.selectedCount', { count: selectedPresets.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              variant={selectedPresets.includes('fast') ? "contained" : "outlined"}
              onClick={() => togglePreset('fast')}
            >
              {t('setup.timeWindow.fast')}
            </Button>
            <Button
              size="small"
              variant={selectedPresets.includes('balanced') ? "contained" : "outlined"}
              onClick={() => togglePreset('balanced')}
            >
              {t('setup.timeWindow.balanced')}
            </Button>
            <Button
              size="small"
              variant={selectedPresets.includes('thorough') ? "contained" : "outlined"}
              onClick={() => togglePreset('thorough')}
            >
              {t('setup.timeWindow.thorough')}
            </Button>
            <Button
              size="small"
              variant="text"
              startIcon={<Icon size={20} d={ICON_FALLBACKS.Restore} />}
              onClick={resetToDefaults}
            >
              {t('setup.timeWindow.reset')}
            </Button>
          </Box>
        </Box>

        {/* Main Settings */}
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label={t('setup.timeWindow.maxTime')}
              type="number"
              value={currentSettings.settings.max_time_minutes}
              onChange={(e) => handleSettingChange(['settings', 'max_time_minutes'], parseInt(e.target.value) || 90)}
              helperText={t('setup.timeWindow.maxTimeHelp')}
              inputProps={{ min: 30, max: 240 }}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              label={t('setup.timeWindow.timeWindow')}
              type="number"
              value={currentSettings.settings.window}
              onChange={(e) => handleSettingChange(['settings', 'window'], parseInt(e.target.value) || 1200)}
              helperText={t('setup.timeWindow.timeWindowHelp')}
              inputProps={{ min: 300, max: 3600 }}
            />
          </Box>
        </Box>

        {/* Advanced Settings */}
        <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)} sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
            <Icon size={20} name="gear" style={{ marginRight: 8 }} />
            <Typography variant="subtitle1">{t('setup.timeWindow.advancedSettings')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.timeWindow.slope')}
                    type="number"
                    value={currentSettings.settings.slope}
                    onChange={(e) => handleSettingChange(['settings', 'slope'], parseFloat(e.target.value) || 2.0)}
                    helperText={t('setup.timeWindow.slopeHelp')}
                    inputProps={{ step: 0.1, min: 0.5, max: 5.0 }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label={t('setup.timeWindow.intercept')}
                    type="number"
                    value={currentSettings.settings.intercept}
                    onChange={(e) => handleSettingChange(['settings', 'intercept'], parseInt(e.target.value) || 1200)}
                    helperText={t('setup.timeWindow.interceptHelp')}
                    inputProps={{ min: 0, max: 3600 }}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label={t('setup.timeWindow.processingTime')}
                type="number"
                value={currentSettings.settings.once_off_processing_time_seconds}
                onChange={(e) => handleSettingChange(['settings', 'once_off_processing_time_seconds'], parseInt(e.target.value) || 15)}
                helperText={t('setup.timeWindow.processingTimeHelp')}
                inputProps={{ min: 0, max: 120 }}
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('setup.timeWindow.passengerProcessingTimes')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('setup.timeWindow.standardPassengers')}
                      type="number"
                      value={standardProcessingTime}
                      onChange={(e) => handleProcessingTimeChange('standard', parseInt(e.target.value) || 30)}
                      inputProps={{ min: 10, max: 300 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      label={t('setup.timeWindow.wheelchairPassengers')}
                      type="number"
                      value={wheelchairProcessingTime}
                      onChange={(e) => handleProcessingTimeChange('wheelchair', parseInt(e.target.value) || 90)}
                      inputProps={{ min: 30, max: 600 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <Trans i18nKey="setup.timeWindow.info" ns="simulation">
              <strong>Time Window Settings</strong> determine how flexible pickup and dropoff times can be. Larger windows allow more ride sharing but may increase wait times.
            </Trans>
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};